import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, formatDate } from "@/lib/format";

const STATUSES = ["pending_payment", "paid", "preparing", "lens_fitting", "ready_to_ship", "shipped", "ready_for_pickup", "picked_up", "delivered", "return_requested", "returned", "refunded", "cancelled"];
const COMMON_STATUSES = ["pending_payment", "paid", "preparing", "lens_fitting", "return_requested", "returned", "refunded", "cancelled"];
const DELIVERY_ONLY = ["ready_to_ship", "shipped", "delivered"];
const PICKUP_ONLY = ["ready_for_pickup", "picked_up"];

function statusOptionsFor(order) {
  const extra = order.fulfilment_method === "pickup" ? PICKUP_ONLY : DELIVERY_ONLY;
  return [...COMMON_STATUSES, ...extra];
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [trackingDrafts, setTrackingDrafts] = useState({});

  const load = () => api.get("/admin/orders", { params: { status: filter || undefined } }).then((res) => setOrders(res.data.items));

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    const tracking = status === "shipped" ? trackingDrafts[id] : undefined;
    await api.put(`/admin/orders/${id}/status`, { status, tracking_number: tracking || undefined });
    load();
  };

  return (
    <div className="p-8" data-testid="admin-orders-page">
      <h1 className="font-serif text-3xl mb-6 text-zinc-900">Orders</h1>
      <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-zinc-200 rounded-full px-4 py-2 text-sm mb-6 text-zinc-700 focus:border-teal-600 outline-none" data-testid="admin-orders-status-filter">
        <option value="">All statuses</option>
        {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-zinc-50 text-xs font-heading uppercase text-zinc-500 text-left">
            <tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Fulfilment</th><th className="p-3">Total</th><th className="p-3">Date</th><th className="p-3">Tracking</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-zinc-100 text-zinc-800" data-testid={`order-row-${o.order_number}`}>
                <td className="p-3 font-medium">{o.order_number}</td>
                <td className="p-3">{o.shipping_address?.full_name}</td>
                <td className="p-3" data-testid={`order-fulfilment-${o.order_number}`}>
                  {o.fulfilment_method === "pickup" ? (
                    <span className="inline-flex items-center rounded-full bg-teal-50 text-teal-700 text-xs font-heading font-semibold uppercase px-2.5 py-1">Pickup — Perth</span>
                  ) : (
                    <span className="text-zinc-500">Delivery</span>
                  )}
                </td>
                <td className="p-3">{money(o.total)}</td>
                <td className="p-3 text-zinc-500">{formatDate(o.created_at)}</td>
                <td className="p-3">
                  {o.tracking_number ? (
                    <span data-testid={`order-tracking-${o.order_number}`}>{o.tracking_number}</span>
                  ) : (
                    <input
                      placeholder="Tracking #"
                      value={trackingDrafts[o.id] || ""}
                      onChange={(e) => setTrackingDrafts({ ...trackingDrafts, [o.id]: e.target.value })}
                      className="border border-zinc-200 rounded-md text-xs px-1.5 py-1 w-24 focus:border-teal-600 outline-none"
                      data-testid={`order-tracking-input-${o.order_number}`}
                    />
                  )}
                </td>
                <td className="p-3">
                  <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="border border-zinc-200 rounded-md text-xs px-1.5 py-1.5 focus:border-teal-600 outline-none" data-testid={`order-status-${o.order_number}`}>
                    {statusOptionsFor(o).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
